'use client';

import React, { useRef, useEffect } from 'react';
import { ExtendedArchiMateDiagram } from '@/lib/archimate/extended-types';
import { getElementMetadata } from '@/lib/archimate/elements';
import { Maximize2, Minimize2 } from 'lucide-react';

interface MiniMapProps {
  diagram: ExtendedArchiMateDiagram;
  viewport: {
    x: number;
    y: number;
    width: number;
    height: number;
    zoom: number;
  };
  onViewportChange: (x: number, y: number) => void;
  isVisible: boolean;
  onToggle: () => void;
}

export default function MiniMap({
  diagram,
  viewport,
  onViewportChange,
  isVisible,
  onToggle,
}: MiniMapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  // Calculate bounds of all elements
  const calculateBounds = () => {
    if (diagram.elements.length === 0) {
      return { minX: 0, minY: 0, maxX: 800, maxY: 600 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    diagram.elements.forEach((element) => {
      const x = element.x || 0;
      const y = element.y || 0;
      const w = element.width || 120;
      const h = element.height || 60;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    });

    // Add padding
    const padding = 50;
    return {
      minX: minX - padding,
      minY: minY - padding,
      maxX: maxX + padding,
      maxY: maxY + padding,
    };
  };

  // Render minimap
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bounds = calculateBounds();
    const contentWidth = bounds.maxX - bounds.minX;
    const contentHeight = bounds.maxY - bounds.minY;

    // Scale to fit canvas
    const scale = Math.min(canvas.width / contentWidth, canvas.height / contentHeight);

    // Clear canvas
    ctx.fillStyle = '#0A1E2E';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw relationships first (so they appear behind elements)
    diagram.relationships.forEach((relationship) => {
      const source = diagram.elements.find((e) => e.id === relationship.sourceId);
      const target = diagram.elements.find((e) => e.id === relationship.targetId);

      if (source && target) {
        const x1 = ((source.x || 0) - bounds.minX) * scale;
        const y1 = ((source.y || 0) - bounds.minY) * scale;
        const x2 = ((target.x || 0) - bounds.minX) * scale;
        const y2 = ((target.y || 0) - bounds.minY) * scale;

        ctx.strokeStyle = 'rgba(232, 152, 65, 0.3)'; // Orange with transparency
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    });

    // Draw elements with their actual colors
    diagram.elements.forEach((element) => {
      const x = ((element.x || 0) - bounds.minX) * scale;
      const y = ((element.y || 0) - bounds.minY) * scale;
      const w = (element.width || 120) * scale;
      const h = (element.height || 60) * scale;

      // Get element metadata for color
      const metadata = getElementMetadata(element.type);
      const baseColor = metadata?.color || '#E89841'; // Fallback to orange

      // Convert hex color to rgba with transparency
      const hexToRgba = (hex: string, alpha: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };

      // Draw element with its actual color
      ctx.fillStyle = hexToRgba(baseColor, 0.7); // Higher opacity for better visibility
      ctx.strokeStyle = hexToRgba(baseColor, 0.9); // Even higher for stroke
      ctx.lineWidth = Math.max(1, scale * 0.5); // Scale-based line width

      // Draw based on shape (simplified for minimap)
      const shape = metadata?.shape || 'box';
      if (shape === 'circle') {
        // Draw as circle
        const radius = Math.min(w, h) / 2;
        ctx.beginPath();
        ctx.arc(x + w / 2, y + h / 2, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      } else if (shape === 'pill') {
        // Draw as rounded pill
        const radius = h / 2;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, radius);
        ctx.fill();
        ctx.stroke();
      } else if (shape === 'rounded') {
        // Draw with rounded corners
        const radius = Math.min(w, h) * 0.2;
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, radius);
        ctx.fill();
        ctx.stroke();
      } else {
        // Draw as rectangle (default)
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x, y, w, h);
      }
    });

    // Draw viewport indicator
    const viewportX = ((viewport.x - bounds.minX) * scale);
    const viewportY = ((viewport.y - bounds.minY) * scale);
    const viewportW = (viewport.width / viewport.zoom) * scale;
    const viewportH = (viewport.height / viewport.zoom) * scale;

    ctx.strokeStyle = '#E89841'; // Orange
    ctx.lineWidth = 2;
    ctx.fillStyle = 'rgba(232, 152, 65, 0.1)';
    ctx.fillRect(viewportX, viewportY, viewportW, viewportH);
    ctx.strokeRect(viewportX, viewportY, viewportW, viewportH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagram, viewport]);

  // Handle drag on minimap to move viewport
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    handleMouseMove(e);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging && e.buttons !== 1) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const bounds = calculateBounds();
    const contentWidth = bounds.maxX - bounds.minX;
    const contentHeight = bounds.maxY - bounds.minY;
    const scale = Math.min(canvas.width / contentWidth, canvas.height / contentHeight);

    // Convert minimap coords to world coords
    const worldX = (mouseX / scale) + bounds.minX - (viewport.width / viewport.zoom / 2);
    const worldY = (mouseY / scale) + bounds.minY - (viewport.height / viewport.zoom / 2);

    onViewportChange(worldX, worldY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!isVisible) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-4 right-4 p-3 bg-background/95 backdrop-blur border border-border rounded-lg hover:bg-background hover:border-accent/40 transition-all shadow-lg z-40"
        title="Show minimap"
      >
        <Maximize2 className="w-5 h-5 text-foreground" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-background/95 backdrop-blur border border-border rounded-lg shadow-2xl z-40 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-background/50">
        <span className="text-xs font-medium text-foreground">Overview</span>
        <button
          onClick={onToggle}
          className="p-1 hover:bg-cream/10 rounded transition-colors"
          title="Hide minimap"
        >
          <Minimize2 className="w-3.5 h-3.5 text-foreground-tertiary" />
        </button>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={200}
        height={150}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        className="cursor-pointer"
        style={{ display: 'block' }}
      />

      {/* Stats */}
      <div className="px-3 py-2 bg-background/50 border-t border-border text-xs text-foreground-tertiary flex items-center justify-between">
        <span>{diagram.elements.length} elements</span>
        <span className="text-accent">{(viewport.zoom * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
}
